<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">财富统计</h1>
            <p class="list-page-description">查看授权范围内的财富客户、资金本金、到期批次和维度分布。</p>
          </div>
          <div class="list-page-heading-actions">
            <button type="button" class="btn-secondary inline-flex items-center gap-2" :disabled="loading" @click="loadStatistics">
              <svg class="h-4 w-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              刷新
            </button>
          </div>
        </div>
      </div>
    </template>

    <div class="mx-6 space-y-6">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <WealthMetricCard title="财富客户数" :value="overview.customerCount || 0" description="授权范围内客户" tone="primary" />
        <WealthMetricCard title="当前总本金" :value="`¥${formatMoney(overview.currentPrincipalAmount)}`" description="有效及已到期未处理批次" tone="emerald" />
        <WealthMetricCard title="累计注资" :value="`¥${formatMoney(overview.totalInvestAmount)}`" description="已落账注资累计" tone="primary" />
        <WealthMetricCard title="累计增资" :value="`¥${formatMoney(overview.totalIncreaseAmount)}`" description="已落账增资累计" tone="indigo" />
        <WealthMetricCard title="累计减资" :value="`¥${formatMoney(overview.totalDecreaseAmount)}`" description="已落账减资累计" tone="amber" />
        <WealthMetricCard title="累计撤资" :value="`¥${formatMoney(overview.totalWithdrawAmount)}`" description="已落账撤资累计" tone="red" />
        <WealthMetricCard title="即将到期" :value="`${overview.upcomingMaturityBatchCount || 0} 批`" :description="`金额 ¥${formatMoney(overview.upcomingMaturityAmount)}`" tone="amber" />
        <WealthMetricCard title="已到期未处理" :value="`${overview.maturedUnhandledBatchCount || 0} 批`" :description="`金额 ¥${formatMoney(overview.maturedUnhandledAmount)}`" tone="red" />
      </div>

      <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DimensionPanel title="按负责人" :rows="ownerRows" />
        <DimensionPanel title="按风险等级" :rows="riskRows" />
        <DimensionPanel title="按客户来源" :rows="sourceRows" />
      </div>

      <WealthBatchPanel
        title="到期批次"
        description="统计页内复用批次面板查看即将到期和已到期未处理批次。"
        mode="upcoming"
        :show-mode-switch="true"
      />
    </div>
  </ListPageShell>
</template>

<script setup>
import { defineComponent, h, onMounted, ref } from 'vue'
import ListPageShell from '@/components/ListPageShell.vue'
import WealthMetricCard from '../components/WealthMetricCard.vue'
import WealthBatchPanel from '../components/WealthBatchPanel.vue'
import {
  getWealthStatisticsByOwner,
  getWealthStatisticsByRiskLevel,
  getWealthStatisticsBySource,
  getWealthStatisticsOverview
} from '@/api/wealth'
import { formatMoney, toNumber } from '../utils'

const DimensionPanel = defineComponent({
  name: 'DimensionPanel',
  props: {
    title: { type: String, required: true },
    rows: { type: Array, default: () => [] }
  },
  setup(props) {
    return () => h('section', { class: 'rounded-lg border border-slate-200 bg-white p-4 shadow-sm' }, [
      h('h3', { class: 'text-sm font-semibold text-slate-900' }, props.title),
      props.rows.length === 0
        ? h('div', { class: 'py-8 text-center text-sm text-slate-500' }, '暂无数据')
        : h('div', { class: 'mt-4 space-y-3' }, props.rows.map(row => {
          const amount = toNumber(row.currentPrincipalAmount)
          const max = Math.max(...props.rows.map(item => toNumber(item.currentPrincipalAmount)), 1)
          const width = `${Math.max(4, Math.round((amount / max) * 100))}%`
          return h('div', { key: row.code || row.name, class: 'space-y-1' }, [
            h('div', { class: 'flex items-center justify-between gap-3 text-sm' }, [
              h('span', { class: 'truncate font-medium text-slate-700' }, row.name || row.code || '未分组'),
              h('span', { class: 'shrink-0 text-xs text-slate-500' }, `${row.customerCount || 0} 人 · ¥${formatMoney(row.currentPrincipalAmount)}`)
            ]),
            h('div', { class: 'h-2 rounded-full bg-slate-100' }, [
              h('div', { class: 'h-2 rounded-full bg-primary-500', style: { width } })
            ])
          ])
        }))
    ])
  }
})

const loading = ref(false)
const overview = ref({})
const ownerRows = ref([])
const riskRows = ref([])
const sourceRows = ref([])

async function loadStatistics() {
  loading.value = true
  try {
    const [overviewResult, owners, risks, sources] = await Promise.all([
      getWealthStatisticsOverview(),
      getWealthStatisticsByOwner(),
      getWealthStatisticsByRiskLevel(),
      getWealthStatisticsBySource()
    ])
    overview.value = overviewResult || {}
    ownerRows.value = owners || []
    riskRows.value = risks || []
    sourceRows.value = sources || []
  } finally {
    loading.value = false
  }
}

onMounted(loadStatistics)
</script>
